/**
 * Геолокация (t_3bd7dec5): утилиты значения и валидации.
 * Value shape: { lat, lng, accuracy?, source, address? } — source: "gps" (браузерный
 * Geolocation API) | "manual" (точка на карте).
 */

export interface GeolocationValue {
	/** Точность GPS в метрах (только для source="gps") */
	accuracy?: number;
	/** Человекочитаемый адрес (обратный геокодинг с клиента, best-effort) */
	address?: string;
	lat: number;
	lng: number;
	source: "gps" | "manual";
}

export type GeolocationMode = "both" | "current" | "map";

export const GEOLOCATION_DEFAULT_PRECISION = 5;
export const GEOLOCATION_MIN_PRECISION = 4;
export const GEOLOCATION_MAX_PRECISION = 6;

/** Радиус ~111 км на градус широты — допуски для диапазонов координат. */
const LAT_MIN = -90;
const LAT_MAX = 90;
const LNG_MIN = -180;
const LNG_MAX = 180;

export function isGeolocationValue(value: unknown): value is GeolocationValue {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return false;
	}
	const v = value as Record<string, unknown>;
	return (
		typeof v.lat === "number" &&
		Number.isFinite(v.lat) &&
		typeof v.lng === "number" &&
		Number.isFinite(v.lng) &&
		(v.source === "gps" || v.source === "manual")
	);
}

export type GeolocationErrorCode =
	| "required"
	| "invalid"
	| "latOutOfRange"
	| "lngOutOfRange";

export interface GeolocationValidationResult {
	code?: GeolocationErrorCode;
	isValid: boolean;
}

export function validateGeolocationValue(
	field: import("@/lib/database").FormField,
	value: unknown
): GeolocationValidationResult {
	const isUnset = (v: unknown) => v === undefined || v === null;
	const empty =
		value === undefined ||
		value === null ||
		value === "" ||
		(typeof value === "object" &&
			!Array.isArray(value) &&
			isUnset((value as Record<string, unknown>).lat) &&
			isUnset((value as Record<string, unknown>).lng));

	if (empty) {
		return field.required
			? { code: "required", isValid: false }
			: { isValid: true };
	}

	if (!isGeolocationValue(value)) {
		return { code: "invalid", isValid: false };
	}

	if (value.lat < LAT_MIN || value.lat > LAT_MAX) {
		return { code: "latOutOfRange", isValid: false };
	}
	if (value.lng < LNG_MIN || value.lng > LNG_MAX) {
		return { code: "lngOutOfRange", isValid: false };
	}

	return { isValid: true };
}

/** «55.01777, 82.94115 (GPS, \u00b121 \u043c)» / «55.10000, 82.90000 (\u0442\u043e\u0447\u043a\u0430 \u043d\u0430 \u043a\u0430\u0440\u0442\u0435)». */
export function formatGeolocationCoords(value: GeolocationValue): string {
	const coords = `${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}`;
	if (value.source === "gps") {
		const acc =
			typeof value.accuracy === "number"
				? `, \u00b1${Math.round(value.accuracy)} \u043c`
				: "";
		return `${coords} (GPS${acc})`;
	}
	return `${coords} (\u0442\u043e\u0447\u043a\u0430 \u043d\u0430 \u043a\u0430\u0440\u0442\u0435)`;
}

/** Округление координат до заданной точности (для отображения). */
export function formatCoordinate(
	value: number,
	precision: number | undefined
): string {
	const p =
		precision && precision >= GEOLOCATION_MIN_PRECISION
			? Math.min(precision, GEOLOCATION_MAX_PRECISION)
			: GEOLOCATION_DEFAULT_PRECISION;
	return value.toFixed(p);
}
